import { useEffect, useState } from "react";
import { Pressable, Text, TextInput, View, type ViewStyle } from "react-native";
import { Minus, Plus } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";
import { getInteractionDelta } from "../utils/interactionStyles";

/**
 * Numeric stepper — `– N +` row with an optional editable center value.
 *
 * Bare on purpose. The audit (task 2.5) lists `Stepper` as a missing
 * primitive; consumers that want a label/error wrapper compose this
 * inside `MoonFormField` themselves, the same way they'd compose any
 * other input. Inline-label compositions (label on the left, counter
 * on the right — like the `OptionalNumber` pattern in AddSpecimenModal)
 * keep working too because this is just a row.
 *
 * Replaces the hand-rolled `MiniCounter` declared inline in
 * AddSpecimenModal (used 6×: retrieval totals, MII/MI/degenerated
 * counts, frozen specimen count, embryo thaw counts).
 *
 * State coverage (Phase 1.0 matrix subset):
 *   - idle:     default
 *   - disabled: opacity 0.5 on the wrapper, presses ignored
 *   - error / focused / loading: not applicable to a counter shape;
 *     consumers needing those compose this inside MoonFormField which
 *     owns those states above the input.
 */
type Props = {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  /** Hue used for the +/– button backgrounds and active border. */
  accentColor?: string;
  /** Step size. Defaults to 1. */
  step?: number;
  /** Optional visual value for an unanswered field, such as "–". */
  displayValue?: string | number;
  /** Render the center value as a bordered numeric input. */
  editable?: boolean;
  /** Accessible label for the editable center value. */
  accessibilityLabel?: string;
  /**
   * Stable analytics identifier for what is being counted. The value itself
   * is never captured — a stepper can hold a clinical count.
   */
  action?: string;
  disabled?: boolean;
  testID?: string;
  style?: ViewStyle;
};

const BUTTON_SIZE = 28;
const ICON_SIZE = 14;

export function MoonStepper({
  value,
  onChange,
  min = 0,
  max,
  accentColor = colors.textMid,
  step = 1,
  displayValue,
  editable = false,
  accessibilityLabel = "Value",
  action,
  disabled,
  testID,
  style,
}: Props) {
  const capturePress = usePressCapture("MoonStepper");
  const initialInputValue =
    displayValue != null ? String(displayValue) : String(value);
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const atMin = value <= min;
  const atMax = max != null && value >= max;

  useEffect(() => {
    if (!isInputFocused) {
      setInputValue(
        displayValue != null ? String(displayValue) : String(value),
      );
    }
  }, [displayValue, isInputFocused, value]);

  const clampValue = (next: number) =>
    max != null ? Math.min(max, Math.max(min, next)) : Math.max(min, next);

  const commitInputValue = (rawValue: string) => {
    const parsed = Number.parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) return;
    const next = clampValue(parsed);
    setInputValue(String(next));
    onChange(next);
  };

  const decrement = () => {
    if (disabled || atMin) return;
    onChange(Math.max(min, value - step));
  };

  const increment = () => {
    if (disabled || atMax) return;
    onChange(max != null ? Math.min(max, value + step) : value + step);
  };

  return (
    <View
      testID={testID}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          opacity: disabled ? 0.5 : 1,
        },
        style,
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Decrement"
        accessibilityState={{ disabled: disabled || atMin }}
        onPress={() => {
          capturePress("Decrement", action);
          decrement();
        }}
        disabled={disabled || atMin}
      >
        {({ hovered, focused }) => (
          <View
            style={[
              {
                width: BUTTON_SIZE,
                height: BUTTON_SIZE,
                borderRadius: BUTTON_SIZE / 2,
                backgroundColor: `${accentColor}12`,
                alignItems: "center",
                justifyContent: "center",
                borderWidth: 1,
                borderColor: atMin ? colors.border : `${accentColor}30`,
              },
              getInteractionDelta({ hovered, focused }),
            ]}
          >
            <Minus
              size={ICON_SIZE}
              color={atMin ? colors.textFaint : accentColor}
            />
          </View>
        )}
      </Pressable>
      {editable ? (
        <TextInput
          value={inputValue}
          onChangeText={(rawValue) => {
            const digitsOnly = rawValue.replace(/\D/g, "");
            setInputValue(digitsOnly);
            if (digitsOnly) commitInputValue(digitsOnly);
          }}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => {
            setIsInputFocused(false);
            if (!inputValue) {
              const fallback = clampValue(min);
              setInputValue(String(fallback));
              onChange(fallback);
            } else {
              commitInputValue(inputValue);
            }
          }}
          accessibilityLabel={accessibilityLabel}
          editable={!disabled}
          keyboardType="number-pad"
          selectTextOnFocus
          selectionColor={accentColor}
          style={{
            width: 52,
            height: 32,
            borderWidth: 1,
            borderColor: disabled ? colors.border : `${accentColor}45`,
            borderRadius: 8,
            backgroundColor: colors.surface,
            paddingHorizontal: 4,
            paddingVertical: 0,
            textAlign: "center",
            fontFamily: typography.families.sansMedium,
            fontSize: 15,
            fontWeight: "600",
            color: colors.text,
          }}
        />
      ) : (
        <Text
          style={{
            minWidth: BUTTON_SIZE,
            textAlign: "center",
            fontFamily: typography.families.sansMedium,
            fontSize: 15,
            fontWeight: "600",
            color: colors.text,
          }}
        >
          {displayValue ?? value}
        </Text>
      )}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Increment"
        accessibilityState={{ disabled: disabled || atMax }}
        onPress={() => {
          capturePress("Increment", action);
          increment();
        }}
        disabled={disabled || atMax}
      >
        {({ hovered, focused }) => (
          <View
            style={[
              {
                width: BUTTON_SIZE,
                height: BUTTON_SIZE,
                borderRadius: BUTTON_SIZE / 2,
                backgroundColor: `${accentColor}12`,
                alignItems: "center",
                justifyContent: "center",
                borderWidth: 1,
                borderColor: atMax ? colors.border : `${accentColor}30`,
              },
              getInteractionDelta({ hovered, focused }),
            ]}
          >
            <Plus
              size={ICON_SIZE}
              color={atMax ? colors.textFaint : accentColor}
            />
          </View>
        )}
      </Pressable>
    </View>
  );
}
